import React, { useEffect, useState, Fragment, Suspense, lazy } from 'react';
import { connect, Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import i18n from 'i18next';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'bootstrap/dist/css/bootstrap.min.css';
import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import '../../scss/app.scss';
import PropTypes from 'prop-types';
import store from './store';
import ScrollToTop from './ScrollToTop';
import { config as i18nextConfig } from '../../translations';
import firebaseConfig from '../../config/firebase';
import BeatLoader from 'react-spinners/BeatLoader';
import { useEmbedAutoHeight } from '../../utils/helpers';

// Lazy load heavy components
const Router = lazy(() => import('./Router'));

// Initialize i18n once
if (!i18n.isInitialized) {
  i18n.init(i18nextConfig);
}

const ThemeComponent = ({ children, themeName }) => {
  const theme = createTheme({
    palette: {
      mode: themeName === 'theme-dark' ? 'dark' : 'light',
    },
  });
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
};

ThemeComponent.propTypes = {
  children: PropTypes.node.isRequired,
  themeName: PropTypes.string.isRequired,
};

const ConnectedThemeComponent = connect(state => ({ themeName: state.theme.className }))(ThemeComponent);

const Loader = ({ className }) => (
  <div className={className} style={{ display: 'flex', flexDirection: 'column', height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
    <BeatLoader color="#17a2b8" loading={true} size={15} />
    <p style={{ fontWeight: 500 }}>Loading... Please wait</p>
  </div>
);

const App = () => {
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  // Report our height to a host framing this checkout, so its scrollbar is
  // the only one. Mounted here rather than per page: the buyer moves between
  // /paynow, /processTransaction and the customer form, and this should not
  // restart underneath them. See useEmbedAutoHeight().
  useEmbedAutoHeight();

  useEffect(() => {
    if (!firebase.apps.length) {
      firebase.initializeApp(firebaseConfig);
    }

    const handleReady = () => {
      setLoading(false);
      setTimeout(() => setLoaded(true), 500);
    };

    if (document.readyState === "complete" || document.readyState === "interactive") {
      handleReady();
    } else {
      document.addEventListener("DOMContentLoaded", handleReady);
    }

    return () => document.removeEventListener("DOMContentLoaded", handleReady);
  }, []);

  return (
    <Fragment>
      <Provider store={store}>
        <BrowserRouter basename="/">
          <I18nextProvider i18n={i18n}>
            <ScrollToTop>
              <Fragment>
                {!loaded && <Loader className="ipayAppLoader" />}
                <ConnectedThemeComponent>
                  <Suspense fallback={<Loader />}>
                    <Router />
                  </Suspense>
                </ConnectedThemeComponent>
              </Fragment>
            </ScrollToTop>
          </I18nextProvider>
        </BrowserRouter>
      </Provider>
    </Fragment>
  );
};

export default App;
